import { useState } from 'react';
import type { ClientProfile, PaymentRecord } from '../types';
import type { AuditLogRecord } from '../page';

interface TimelineEvent {
  id: string;
  label: string;
  detail?: string;
  date: string;
  icon: string;
  iconCls: string;
  category: string;
}

// ── Parse audit log details safely ──────────────────────────────────────────
function parseDetails(raw: AuditLogRecord['details']): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw as Record<string, unknown>;
}

// ── Map a single audit_log record to a display event ────────────────────────
function auditToEvent(log: AuditLogRecord): TimelineEvent | null {
  const d = parseDetails(log.details);

  // Onboarding completed
  if (log.action === 'onboarding_complete') {
    const provider = (d.provider as string) ?? 'email';
    return {
      id: `audit-${log.id}`,
      label: 'Onboarding concluído',
      detail: `Usuário completou o assistente de configuração inicial${provider === 'google' ? ' (Google)' : ''}`,
      date: log.created_at,
      icon: 'ri-check-double-line',
      iconCls: 'bg-emerald-500/20 text-emerald-400',
      category: 'Cadastro',
    };
  }

  // Login
  if (log.action === 'login') {
    const provider = (d.provider as string) ?? 'email';
    return {
      id: `audit-${log.id}`,
      label: 'Login realizado',
      detail: provider === 'google' ? 'Acesso via conta Google' : 'Acesso via e-mail e senha',
      date: log.created_at,
      icon: 'ri-login-box-line',
      iconCls: 'bg-sky-500/20 text-sky-400',
      category: 'Acesso',
    };
  }

  // Logout
  if (log.action === 'logout') {
    return {
      id: `audit-${log.id}`,
      label: 'Logout realizado',
      detail: 'Usuário saiu da conta manualmente',
      date: log.created_at,
      icon: 'ri-logout-box-r-line',
      iconCls: 'bg-slate-500/20 text-slate-400',
      category: 'Acesso',
    };
  }

  // Profile / address edits
  if (log.action === 'update' && log.resource === 'user') {
    const section = d.section as string | undefined;
    if (section === 'personal_data') {
      return {
        id: `audit-${log.id}`,
        label: 'Dados pessoais atualizados',
        detail: 'Perfil pessoal editado',
        date: log.created_at,
        icon: 'ri-user-settings-line',
        iconCls: 'bg-violet-500/20 text-violet-400',
        category: 'Perfil',
      };
    }
    if (section === 'address') {
      return {
        id: `audit-${log.id}`,
        label: 'Endereço atualizado',
        detail: 'Endereço de cadastro alterado',
        date: log.created_at,
        icon: 'ri-map-pin-line',
        iconCls: 'bg-orange-500/20 text-orange-400',
        category: 'Perfil',
      };
    }
  }

  // Settings changes
  if (log.action === 'settings_change') {
    const type = d.type as string | undefined;

    if (type === 'pin_activated') {
      return {
        id: `audit-${log.id}`,
        label: 'PIN de segurança ativado',
        detail: 'Bloqueio por PIN configurado',
        date: log.created_at,
        icon: 'ri-lock-password-line',
        iconCls: 'bg-emerald-500/20 text-emerald-400',
        category: 'PIN',
      };
    }
    if (type === 'pin_deactivated') {
      return {
        id: `audit-${log.id}`,
        label: 'PIN de segurança desativado',
        detail: 'Bloqueio por PIN removido',
        date: log.created_at,
        icon: 'ri-lock-unlock-line',
        iconCls: 'bg-amber-500/20 text-amber-400',
        category: 'PIN',
      };
    }
    if (type === 'pin_changed') {
      return {
        id: `audit-${log.id}`,
        label: 'PIN de segurança alterado',
        detail: 'Código PIN atualizado',
        date: log.created_at,
        icon: 'ri-key-2-line',
        iconCls: 'bg-violet-500/20 text-violet-400',
        category: 'PIN',
      };
    }
    if (type === 'pin_timeout_changed') {
      const mins = d.timeout_minutes as number | undefined;
      return {
        id: `audit-${log.id}`,
        label: 'Tempo de bloqueio do PIN alterado',
        detail: mins ? `Novo intervalo: ${mins} minuto${mins !== 1 ? 's' : ''}` : 'Tempo de inatividade atualizado',
        date: log.created_at,
        icon: 'ri-timer-line',
        iconCls: 'bg-sky-500/20 text-sky-400',
        category: 'PIN',
      };
    }
    if (type === 'password_changed') {
      return {
        id: `audit-${log.id}`,
        label: 'Senha alterada',
        detail: 'Senha de acesso atualizada',
        date: log.created_at,
        icon: 'ri-lock-line',
        iconCls: 'bg-red-500/20 text-red-400',
        category: 'Segurança',
      };
    }
  }

  return null;
}

// ── Build static events from profile + payments ──────────────────────────────
function buildStaticEvents(profile: ClientProfile, payments: PaymentRecord[], hasOnboardingAudit: boolean): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  events.push({
    id: 'created',
    label: 'Conta criada',
    detail: `Cadastro realizado via ${profile.auth_provider === 'google' ? 'Google' : 'e-mail e senha'}`,
    date: profile.created_at,
    icon: 'ri-user-add-line',
    iconCls: 'bg-violet-500/20 text-violet-400',
    category: 'Cadastro',
  });

  // Only show static fallback if there's no real audit_log for onboarding_complete.
  // Old accounts (created before the audit log was implemented) will still show this
  // using profile.created_at as a safe approximation.
  if (profile.wizard_completed && !hasOnboardingAudit) {
    events.push({
      id: 'onboarding',
      label: 'Onboarding concluído',
      detail: 'Usuário completou o assistente de configuração inicial',
      // ⚠ profile.updated_at changes on every profile write — use created_at as safer fallback
      date: profile.created_at,
      icon: 'ri-check-double-line',
      iconCls: 'bg-emerald-500/20 text-emerald-400',
      category: 'Cadastro',
    });
  }

  payments.forEach((p) => {
    if (p.status === 'approved') {
      events.push({
        id: `pay-ok-${p.id}`,
        label: 'Pagamento aprovado',
        detail: `R$ ${Number(p.amount).toFixed(2)}${p.plan_name ? ` · ${p.plan_name}` : ''}${p.gateway ? ` · ${p.gateway}` : ''}`,
        date: p.created_at,
        icon: 'ri-shield-check-line',
        iconCls: 'bg-emerald-500/20 text-emerald-400',
        category: 'Pagamento',
      });
    } else if (p.status === 'rejected') {
      events.push({
        id: `pay-fail-${p.id}`,
        label: 'Pagamento recusado',
        detail: `R$ ${Number(p.amount).toFixed(2)}${p.gateway ? ` · ${p.gateway}` : ''}${p.notes ? ` · ${p.notes}` : ''}`,
        date: p.created_at,
        icon: 'ri-close-circle-line',
        iconCls: 'bg-red-500/20 text-red-400',
        category: 'Pagamento',
      });
    } else if (p.status === 'pending' || p.status === 'in_process') {
      events.push({
        id: `pay-pend-${p.id}`,
        label: p.status === 'in_process' ? 'Pagamento em análise' : 'Pagamento pendente',
        detail: `R$ ${Number(p.amount).toFixed(2)}${p.gateway ? ` · ${p.gateway}` : ''}`,
        date: p.created_at,
        icon: 'ri-time-line',
        iconCls: 'bg-amber-500/20 text-amber-400',
        category: 'Pagamento',
      });
    } else if (p.status === 'refunded') {
      events.push({
        id: `pay-ref-${p.id}`,
        label: 'Pagamento estornado',
        detail: `R$ ${Number(p.amount).toFixed(2)}${p.gateway ? ` · ${p.gateway}` : ''}`,
        date: p.created_at,
        icon: 'ri-arrow-go-back-line',
        iconCls: 'bg-orange-500/20 text-orange-400',
        category: 'Pagamento',
      });
    }
  });

  if (profile.is_blocked) {
    events.push({
      id: 'blocked',
      label: 'Conta bloqueada',
      detail: 'Conta bloqueada por um administrador',
      date: profile.updated_at || profile.created_at,
      icon: 'ri-lock-line',
      iconCls: 'bg-red-500/20 text-red-400',
      category: 'Segurança',
    });
  }

  return events;
}

// ── Formatters ───────────────────────────────────────────────────────────────
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (d: string) => {
  const dt = new Date(d);
  return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// ── Category badge colors ────────────────────────────────────────────────────
const CATEGORY_STYLE: Record<string, string> = {
  Acesso: 'bg-sky-500/10 text-sky-400',
  Cadastro: 'bg-violet-500/10 text-violet-400',
  Onboarding: 'bg-emerald-500/10 text-emerald-400',
  Pagamento: 'bg-emerald-500/10 text-emerald-400',
  Segurança: 'bg-red-500/10 text-red-400',
  PIN: 'bg-amber-500/10 text-amber-400',
  Perfil: 'bg-violet-500/10 text-violet-400',
};

// ── All filter options (derived from categories that actually exist) ──────────
const ALL_FILTER = 'Todos';
const FILTER_OPTIONS = [ALL_FILTER, 'Acesso', 'Perfil', 'PIN', 'Segurança', 'Pagamento', 'Cadastro'];

interface TimelineTabProps {
  profile: ClientProfile;
  payments: PaymentRecord[];
  auditLogs: AuditLogRecord[];
}

export default function TimelineTab({ profile, payments, auditLogs }: TimelineTabProps) {
  const [activeFilter, setActiveFilter] = useState<string>(ALL_FILTER);

  // Merge static events + audit events
  const hasOnboardingAudit = auditLogs.some((l) => l.action === 'onboarding_complete');
  const staticEvents = buildStaticEvents(profile, payments, hasOnboardingAudit);
  const auditEvents = auditLogs.map(auditToEvent).filter((e): e is TimelineEvent => e !== null);

  // Deduplicate by id and sort newest first
  const allEventsMap = new Map<string, TimelineEvent>();
  [...staticEvents, ...auditEvents].forEach((e) => {
    if (!allEventsMap.has(e.id)) allEventsMap.set(e.id, e);
  });
  const allEvents = Array.from(allEventsMap.values()).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Apply filter
  const events = activeFilter === ALL_FILTER
    ? allEvents
    : allEvents.filter((e) => e.category === activeFilter);

  // Count per category for badges
  const countByCategory = allEvents.reduce<Record<string, number>>((acc, e) => {
    acc[e.category] = (acc[e.category] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-2xl">
      {/* Header + count */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[13px] font-600 text-white/50 uppercase tracking-wider">
          {events.length} evento{events.length !== 1 ? 's' : ''}
          {activeFilter !== ALL_FILTER ? ` · ${activeFilter}` : ' registrados'}
        </h3>
        <span className="text-[12px] text-white/30">mais recente primeiro</span>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        {FILTER_OPTIONS.map((opt) => {
          const count = opt === ALL_FILTER ? allEvents.length : (countByCategory[opt] ?? 0);
          const isActive = activeFilter === opt;
          return (
            <button
              key={opt}
              onClick={() => setActiveFilter(opt)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-500 transition-all cursor-pointer whitespace-nowrap border ${
                isActive
                  ? 'bg-white/15 border-white/30 text-white'
                  : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white/70'
              }`}
            >
              {opt}
              {count > 0 && (
                <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-white/40'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {events.length === 0 && (
        <div className="flex flex-col items-center py-16 text-center">
          <i className="ri-history-line text-4xl text-white/20 mb-3" />
          <p className="text-[14px] text-white/30 font-500">
            {activeFilter === ALL_FILTER
              ? 'Nenhum evento registrado'
              : `Nenhum evento de "${activeFilter}" encontrado`}
          </p>
          {activeFilter !== ALL_FILTER && (
            <button
              onClick={() => setActiveFilter(ALL_FILTER)}
              className="mt-3 text-[12px] text-white/40 hover:text-white/70 underline cursor-pointer transition-colors"
            >
              Ver todos os eventos
            </button>
          )}
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 && (
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-4 top-4 bottom-4 w-px bg-white/10" />

          <div className="space-y-0">
            {events.map((event, i) => {
              const catStyle = CATEGORY_STYLE[event.category] ?? 'bg-white/10 text-white/40';
              return (
                <div key={event.id} className="flex gap-4 relative">
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 mt-0.5 ${event.iconCls}`}>
                    <i className={`${event.icon} text-[13px]`} />
                  </div>

                  {/* Content */}
                  <div className={`pb-5 flex-1 ${i === events.length - 1 ? 'pb-0' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[14px] font-600 text-white">{event.label}</p>
                        <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${catStyle}`}>
                          {event.category}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-[11px] text-white/40">{fmtDate(event.date)}</span>
                        <span className="text-[10px] text-white/25 block">{fmtTime(event.date)}</span>
                      </div>
                    </div>
                    {event.detail && (
                      <p className="text-[12px] text-white/50 mt-0.5 leading-relaxed">{event.detail}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
